<template>

    <ion-list :inset="true">
        <ion-list-header class="small-list-header">
            <ion-label class="my-0">{{ description ? description : 'Afbeeldingen' }}</ion-label>

            <ion-button v-if="images_array.length > 0 && !disabled" size="small" class="ma-0 pr-0" color="primary"
                @click="deleteAllImages()">
                <mdicon class="small-icon mr-1" slot="icon-only" name="select-all"></mdicon> Alle verwijderen
            </ion-button>
        </ion-list-header>
        <div class="flex pt-2 pl-2 background-custom-white">

            <div class="image-row">
                <template v-if="!loading">
                    <div v-for="image in sortedImagesArray" :key="image.uuid" v-ripple @click="!disabled && openImage(image)"
                        class="image-container" style="min-width: 100px;" :class="{ 'disabled-container': disabled }">
                        <lazy-image :uuid="image.uuid" img-class="image-class" />
                        <!-- Upload status indicator - only show when upload_url is provided (edit mode) -->
                        <div v-if="upload_url && image.upload_status && image.upload_status !== 'confirmed'" class="upload-status-badge">
                            <mdicon v-if="image.upload_status === 'uploading'" name="loading" size="16" :spin="true" class="text-primary"></mdicon>
                            <mdicon v-else-if="image.upload_status === 'uploaded'" name="check" size="16" class="text-success"></mdicon>
                            <mdicon v-else-if="image.upload_status === 'cors_error'" name="block-helper" size="16" class="text-danger" :title="'CORS error - S3 needs configuration'"></mdicon>
                            <mdicon v-else-if="image.upload_status === 'failed'" name="alert-circle" size="16" class="text-warning"></mdicon>
                            <mdicon v-else-if="image.upload_status === 'pending'" name="clock-outline" size="16" class="text-medium"></mdicon>
                        </div>
                        <div v-if="!disabled" class="delete-badge" @click.stop="deleteImage(image.uuid)">
                            <mdicon name="close" size="16" class="text-danger"></mdicon>
                        </div>
                    </div>
                </template>

                <!-- when loading, we show images skeletom -->
                <div v-if="loading && this.images_array.length > 0" class="image-container" style="min-width: 100px;">
                    <div class="skeleton-image shimmer"></div>
                    <div class="skeleton-image shimmer"></div>
                    <div class="skeleton-image shimmer"></div>
                </div>

            </div>

            <div v-if="!disabled" class="image-container mr-2">
                <div @click="takePhoto()" class="new-image image-class">
                    <mdicon :name="$platform === 'web' ? 'file-upload' : 'camera-plus'" size="24"></mdicon>
                    <span>{{ $platform === 'web' ? 'Upload afbeelding' : 'Afbeelding toevoegen' }}</span>
                </div>
            </div>
            
            <div v-else class="image-container mr-2">
                <div class="new-image image-class disabled-image">
                    <mdicon name="lock" size="24" class="text-medium"></mdicon>
                    <span class="text-medium">Selecteer eerst component</span>
                </div>
            </div>

        </div>
    </ion-list>
</template>
<script>
import { db } from "@/services/db";
import Camera from '@/components/Camera.vue';
import LazyImage from '@/components/LazyImage.vue';
import { alertController, modalController, IonImg } from '@ionic/vue';

export default {
    props: ['imageable_uuid', 'disabled', 'dont_add', 'square', 'description', 'upload_url'],
    components: {
        IonImg,
        LazyImage
    },
    data() {
        return {
            debounceTimeout: null,
            images_array: [],
            loading: true,
            imageUpdateInterval: null,
            isVisible: true,
        }
    },
    created() {
        // LazyImage components handle their own image loading now
        // No need for base64Cache - saves memory!
    },
    mounted() {
        this.loadImages();
        this.setupVisibilityTracking();
        this.subscribeToImageUpdates();
    },
    activated() {
        // Component re-activated (if parent uses <keep-alive>)
        console.log('[ImageArray] Component activated');

        const hasPendingUploads = this.images_array.some(img =>
            img.upload_status &&
            img.upload_status !== 'confirmed' &&
            img.upload_status !== 'failed' &&
            img.upload_status !== 'cors_error'
        );

        if (hasPendingUploads && !this.imageUpdateInterval) {
            console.log('[ImageArray] Pending uploads found, restarting polling');
            this.subscribeToImageUpdates();
        }
    },
    deactivated() {
        // Component de-activated (if parent uses <keep-alive>)
        console.log('[ImageArray] Component deactivated, stopping polling');
        this.stopPolling();
    },
    beforeUnmount() {
        console.log('[ImageArray] beforeUnmount called, cleaning up...');

        // CRITICAL: Stop polling first
        if (this.imageUpdateInterval) {
            clearInterval(this.imageUpdateInterval);
            this.imageUpdateInterval = null;
        }

        // CRITICAL: Remove event listener that keeps component in memory
        if (this.visibilityHandler) {
            document.removeEventListener('visibilitychange', this.visibilityHandler);
            this.visibilityHandler = null;
        }

        // Clear reactive arrays
        // Note: LazyImage components will clean up their own memory in their beforeUnmount
        this.images_array = [];

        // Force release of DOM element references to prevent detached DOM leaks
        if (this.$el) {
            // Remove all child elements to break references
            while (this.$el.firstChild) {
                this.$el.removeChild(this.$el.firstChild);
            }
        }
    },
    watch: {
        // Watch for imageable_uuid changes (when navigating between observations)
        imageable_uuid: {
            handler(newUuid, oldUuid) {
                if (newUuid !== oldUuid && newUuid) {
                    console.log('[ImageArray] imageable_uuid changed, reloading metadata');

                    // Stop polling for old observation
                    this.stopPolling();

                    // Clear old images array (LazyImage components will clean themselves up)
                    this.images_array = [];

                    // Load metadata for new observation
                    this.loadImages();

                    // Restart polling for new observation
                    this.subscribeToImageUpdates();
                }
            },
            immediate: false
        }
    },
    methods: {
        async openImage(image) {
            this.$router.push({ name: 'image-editor', query: { image_uuid: image.uuid } });
        },
        async takePhoto() {
            const modal = await modalController.create({
                component: Camera,
                cssClass: 'camera-modal',
                componentProps: {
                    imageable_uuid: this.imageable_uuid,
                    upload_url: this.upload_url,
                    square: this.square
                },
                animated: false
            });

            await modal.present();

            // Wait for the modal to fully dismiss and handle returned data
            const { data, role } = await modal.onDidDismiss();
            // For non-DB flows (e.g., direct createImages), emit event
            if (data && !this.upload_url) {
                this.$emit('createImages', data);
            }
            // If processed images were returned, append them immediately
            if (data && Array.isArray(data.images)) {
                // LazyImage will load base64 on-demand - no need to cache here!
                // This saves 24MB+ of immediate memory usage

                // Add only lightweight metadata to reactive array
                const newLightweightImages = data.images.map(img => ({
                    uuid: img.uuid,
                    created_at: img.created_at,
                    upload_status: img.upload_status,
                    imageable_uuid: img.imageable_uuid,
                }));

                this.images_array = this.images_array.concat(newLightweightImages);

                // Restart polling if stopped
                if (!this.imageUpdateInterval) {
                    console.log('[ImageArray] New images added, restarting polling');
                    this.subscribeToImageUpdates();
                }

                // Trigger component count refresh to update image icon
                this.$store.dispatch('setComponentCountKey');
                this.$nextTick(() => {
                    const row = this.$el.querySelector('.image-row');
                    if (row) row.scrollLeft = row.scrollWidth;
                });
            } else {
                // Otherwise, reload from the database once writes are complete
                await this.loadImages();
                // Trigger component count refresh after loading
                if (this.images_array.length > 0) {
                    this.$store.dispatch('setComponentCountKey');
                }
            }
        },
        async loadImages() {
            // Guard: don't load if imageable_uuid is invalid
            if (!this.imageable_uuid || typeof this.imageable_uuid !== 'string') {
                this.loading = false;
                return;
            }

            this.loading = true;

            // CRITICAL: Use cursor-based iteration instead of .toArray()
            // This prevents creating a massive temporary array (8 images × 3MB = 24MB)
            // that stays in memory until GC runs
            const lightweightImages = [];

            await db.images
                .where({ imageable_uuid: this.imageable_uuid })
                .each(img => {
                    // OPTIMIZATION: Don't load base64 here - LazyImage will load it on-demand
                    // This prevents loading 8 × 3MB = 24MB into JavaScript memory immediately

                    // Only collect lightweight metadata (uuid, status, timestamps)
                    lightweightImages.push({
                        uuid: img.uuid,
                        created_at: img.created_at,
                        upload_status: img.upload_status,
                        imageable_uuid: img.imageable_uuid,
                    });
                    // img object goes out of scope and can be GC'd immediately
                });

            // Store only lightweight metadata in reactive array
            this.images_array = lightweightImages;

            // Auto-scroll to the newest images at the end of the row
            this.$nextTick(() => {
                const row = this.$el.querySelector('.image-row');
                if (row) {
                    row.scrollLeft = row.scrollWidth;
                }
            });

            setTimeout(() => {
                this.loading = false;
            }, 500);
        },

        setupVisibilityTracking() {
            this.visibilityHandler = () => {
                this.isVisible = !document.hidden;
            };
            document.addEventListener('visibilitychange', this.visibilityHandler);
        },

        stopPolling() {
            if (this.imageUpdateInterval) {
                console.log('[ImageArray] Stopping polling interval');
                clearInterval(this.imageUpdateInterval);
                this.imageUpdateInterval = null;
            }
        },

        subscribeToImageUpdates() {
            // Poll for changes in upload status every 2 seconds
            // This will update the status badges when sync completes
            this.imageUpdateInterval = setInterval(() => {
                // Skip if tab is not visible
                if (!this.isVisible) {
                    return;
                }

                // Check if we have pending uploads
                const hasPendingUploads = this.images_array.some(img =>
                    img.upload_status &&
                    img.upload_status !== 'confirmed' &&
                    img.upload_status !== 'failed' &&
                    img.upload_status !== 'cors_error'
                );

                if (hasPendingUploads) {
                    this.refreshImages();
                } else {
                    // All uploads complete - stop polling!
                    console.log('[ImageArray] All uploads complete, stopping polling');
                    this.stopPolling();
                }
            }, 2000); // Check every 2 seconds
        },
        
        async refreshImages() {
            // Guard: don't refresh if imageable_uuid is invalid
            if (!this.imageable_uuid || typeof this.imageable_uuid !== 'string') {
                return;
            }

            // OPTIMIZATION: Use cursor-based iteration to avoid creating large temporary arrays
            // This prevents 18MB+ arrays from accumulating in memory during polling

            // First, quick count check
            const currentCount = await db.images
                .where({ imageable_uuid: this.imageable_uuid })
                .count();

            // If count changed, we need to reload everything
            if (currentCount !== this.images_array.length) {
                await this.loadImages();
                return;
            }

            // Count is same - just check for status updates
            // Use each() instead of toArray() to avoid creating large array in memory
            const statusUpdates = [];

            await db.images
                .where({ imageable_uuid: this.imageable_uuid })
                .each(img => {
                    // Only extract the minimal data we need for comparison
                    statusUpdates.push({
                        uuid: img.uuid,
                        upload_status: img.upload_status
                    });
                    // Note: img object goes out of scope immediately after this iteration
                    // allowing it to be garbage collected faster
                });

            // Apply status updates to existing lightweight array
            for (const update of statusUpdates) {
                const existing = this.images_array.find(img => img.uuid === update.uuid);
                if (existing && existing.upload_status !== update.upload_status) {
                    existing.upload_status = update.upload_status;
                }
            }
        },
        async deleteImage(uuid) {
            const alert = await alertController.create({
                header: 'Weet je zeker?',
                message: 'Weet je zeker dat je deze afbeelding wilt verwijderen?',
                buttons: [
                    {
                        text: 'Annuleren',
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            console.log('Confirm Cancel');
                        }
                    }, {
                        text: 'Verwijderen',
                        role: 'destructive',
                        handler: async () => {
                            // Remove from non-reactive cache
                            if (this.base64Cache && this.base64Cache.has(uuid)) {
                                this.base64Cache.delete(uuid);
                            }

                            await this.$requests.delete('images', '/images/' + uuid, uuid)
                            this.loadImages();
                            // Trigger component count refresh to update image icon
                            this.$store.dispatch('setComponentCountKey');
                        }
                    }
                ]
            });

            await alert.present();
        },

        async deleteAllImages() {
            const alert = await alertController.create({
                header: 'Weet je zeker?',
                message: 'Weet je zeker dat je alle afbeeldingen wilt verwijderen?',
                buttons: [
                    {
                        text: 'Annuleren',
                        role: 'cancel',
                        cssClass: 'secondary',
                        handler: () => {
                            console.log('Confirm Cancel');
                        }
                    }, {
                        text: 'Verwijderen',
                        role: 'destructive',
                        handler: async () => {
                            // await this.$requests.delete('images', '/images', this.imageable_uuid)
                            // one request per image
                            for (let image of this.images_array) {
                                await this.$requests.delete('images', '/images/' + image.uuid, image.uuid)
                            }
                            this.loadImages();
                            // Trigger component count refresh to update image icon
                            this.$store.dispatch('setComponentCountKey');
                        }
                    }
                ]
            });

            await alert.present();
        }
    },
    computed: {
        sortedImagesArray() {
            // Return a new array sorted by creation time (oldest first)
            return this.images_array.slice().sort((a, b) => {
                const da = new Date(a.created_at);
                const db = new Date(b.created_at);
                return da - db;
            });
        }
    }
}
</script>
<style>
.image-row {
    background-color: var(--ion-color-custom-white);
    overflow-x: scroll;
    display: flex;
    justify-content: left;
    align-items: center;
    border-radius: 10px;
    overflow-x: auto;
    white-space: nowrap;
}

.center {
    display: block;
    margin-left: auto;
    margin-right: auto;
    width: 30%;
    margin: 15px;
    border-radius: 4px;
}

.empty-image-array {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 103px;
}

.skeleton-image {
    width: 80px;
    height: 80px;
    background-color: var(--ion-color-light);
    border-radius: 10px;
    animation: skeleton-loading 1s infinite;
    display: inline-block;
    margin-right: 15px
}

/* first skeleton image has margin left */

.skeleton-image:first-child {
    margin-left: 10px;
}

/* Enhanced shimmer effect with smoother animation */
.shimmer {
    background: linear-gradient(90deg,
            var(--ion-color-medium-tint) 0%,
            var(--ion-color-light) 50%,
            var(--ion-color-medium-tint) 100%);
    background-size: 200% 100%;
    animation: shimmer-effect 1.5s infinite ease-in-out;
}

@keyframes shimmer-effect {
    0% {
        background-position: 100% 0;
    }

    100% {
        background-position: -100% 0;
    }
}

/* Apply shimmer specifically to skeleton images for better focus */
.skeleton-image.shimmer {
    border-radius: 10px;
    opacity: 0.8;
}

/* Optional: Add a subtle box-shadow for depth */
.skeleton-image {
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.delete-badge {
    cursor: pointer;
    display: flex;
    position: absolute;
    align-items: center;
    background-color: var(--ion-color-custom-white);
    justify-content: center;
    top: -12px;
    right: -10px;
    border-radius: 100%;
    height: 25px;
    width: 25px;
}

.upload-status-badge {
    position: absolute;
    bottom: 4px;
    right: 4px;
    background-color: var(--ion-color-custom-white);
    border-radius: 50%;
    width: 24px !important;
    height: 24px !important;
    min-width: 24px !important;
    min-height: 24px !important;
    max-width: 24px !important;
    max-height: 24px !important;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    z-index: 10;
    animation: pulse 2s infinite;
    flex-shrink: 0;
    overflow: hidden;
    padding: 0 !important;
}

.upload-status-badge mdicon {
    width: 16px !important;
    height: 16px !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
}

/* Pulse animation for uploading status */
.upload-status-badge mdicon[name="loading"] {
    animation: spin 1s linear infinite;
}

@keyframes pulse {
    0% { opacity: 0.9; }
    50% { opacity: 1; }
    100% { opacity: 0.9; }
}

@keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
}

/* Disabled state styling */
.disabled-image {
    opacity: 0.5;
    cursor: not-allowed !important;
    background-color: var(--ion-color-light);
}

.disabled-image span {
    font-size: 12px;
}

.disabled-container {
    opacity: 0.5;
    cursor: not-allowed !important;
}
</style>
